import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import ReceivementCard from '../../../../../../components/ReceivementCard'

import { ReceivementPurchaseTable } from '../../../../components/ReceivementTables'
import { useSelector } from 'src/utils/store'
import { actions as appActions } from 'src/features/state'
import { SelectMui } from 'src/components/SelectMui'
import * as S from './ProductsList.styles'
import * as selectors from '../../../../ReceivementSelectors'
import { OverviewProductStatusEnum } from '../../../../ReceivementTypes'
import { isKgOverWithinTolerance } from '../../../../components/ReceivementTables/ReceivementPurchaseTable/isKgOverWithinTolerance'

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 30, 50].map((n) => ({ id: n, description: String(n) }))

const ProductsList = React.memo(() => {
  const dispatch = useDispatch()
  const salesOverview = useSelector(
    (state) => state.receivement.salesOverview,
  )
  const pendingOnlyDivergents = useSelector(selectors.selectOnlyDivergents)
  const pendingProductDescription = useSelector(selectors.selectProductDescription)
  const [activeFilters, setActiveFilters] = useState({ onlyDivergents: false, productDescription: '' })
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [goToInput, setGoToInput] = useState('')
  const isNavigating = useRef(false)
  const isFirstFilterEffect = useRef(true)

  const allProducts = salesOverview?.products ?? []
  const products = allProducts.filter((p) => {
    if (activeFilters.onlyDivergents && (p.status === OverviewProductStatusEnum.CORRECT || isKgOverWithinTolerance(p))) return false
    if (activeFilters.productDescription && !p.productDescription.toLowerCase().includes(activeFilters.productDescription.toLowerCase())) return false
    return true
  })

  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false
      return
    }
    isNavigating.current = true
    dispatch(appActions.isLoading(true))
    requestAnimationFrame(() => {
      // double rAF garante que o browser pintou o loader antes do render pesado
      requestAnimationFrame(() => {
        setActiveFilters({ onlyDivergents: pendingOnlyDivergents, productDescription: pendingProductDescription })
        setCurrentPage(1)
      })
    })
  }, [pendingOnlyDivergents, pendingProductDescription, dispatch])

  const totalPages = Math.max(1, Math.ceil(products.length / pageSize))
  const pageProducts = products.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const goToInputPage = parseInt(goToInput, 10)
  const goToPageValid = totalPages > 1 && !Number.isNaN(goToInputPage) && goToInputPage >= 1 && goToInputPage <= totalPages && goToInputPage !== currentPage

  useEffect(() => {
    if (!isNavigating.current) return
    dispatch(appActions.isLoading(false))
    isNavigating.current = false
  }, [currentPage, pageSize, activeFilters, dispatch])

  const goToPage = (page: number) => {
    if (isNavigating.current || totalPages <= 1) return
    const target = page < 1 ? totalPages : page > totalPages ? 1 : page
    isNavigating.current = true
    dispatch(appActions.isLoading(true))
    requestAnimationFrame(() => {
      // double rAF garante que o browser pintou o loader antes do render pesado
      requestAnimationFrame(() => {
        setCurrentPage(target)
      })
    })
  }

  const handleGoToPage = () => {
    if (!goToPageValid) return
    goToPage(goToInputPage)
    setGoToInput('')
  }

  const handlePageSizeChange = (e: { target: { value: unknown } }) => {
    if (isNavigating.current) return
    const newSize = e.target.value as number
    isNavigating.current = true
    dispatch(appActions.isLoading(true))
    requestAnimationFrame(() => {
      // double rAF garante que o browser pintou o loader antes do render pesado
      requestAnimationFrame(() => {
        setPageSize(newSize)
        setCurrentPage(1)
        setGoToInput('')
      })
    })
  }

  const paginationBar = products.length > 0 && (
    <S.PaginationBar>
      <S.PaginationSection>
        <SelectMui
          label='Itens por página'
          options={PAGE_SIZE_OPTIONS}
          value={pageSize}
          onChange={handlePageSizeChange}
          optionNone={false}
          fullWidth={false}
          formControlSx={{ minWidth: 180 }}
          dataTestId='page-size-select'
        />
      </S.PaginationSection>
      <S.PaginationSection>
        <S.SectionLabel>Ir para:</S.SectionLabel>
        <S.GoToInput
          type='number'
          min={1}
          max={totalPages}
          value={goToInput}
          onChange={(e) => setGoToInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleGoToPage() }}
        />
        <S.CustomButton onClick={handleGoToPage} disabled={!goToPageValid}>
          Ir
        </S.CustomButton>
      </S.PaginationSection>
      <S.PaginationSection>
        <S.CustomButton onClick={() => goToPage(currentPage - 1)} disabled={totalPages <= 1}>
          Anterior
        </S.CustomButton>
        <S.PageLabel>{currentPage}/{totalPages}</S.PageLabel>
        <S.CustomButton onClick={() => goToPage(currentPage + 1)} disabled={totalPages <= 1}>
          Próxima
        </S.CustomButton>
      </S.PaginationSection>
    </S.PaginationBar>
  )

  return (
    <>
      {paginationBar}
      {pageProducts.map((product) => (
        <ReceivementCard
          key={`${product.idItemOrder ?? product.batches[0].idsBatch}`}
          product={product}
          BatchesTable={ReceivementPurchaseTable}
        />
      ))}
      {paginationBar}
    </>
  )
})

export { ProductsList }
